import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { CHART_AC, CHART_DC, CHART_NEUTRAL } from "../../../../styles/theme_constants";
import { comparisonLabel } from "../../../../utils/fleetPeriods";
import {
  change,
  energyUnitFor,
  formatEnergy,
  formatSignedNumber,
  inUnit,
  perDay,
  perDayPerOnlineChargePoint,
} from "../../../../utils/fleetDerivations";
import { axisLabel, hasUnclassified, splitDrawable } from "./panelHelpers";
import type { PanelProps } from "./panelHelpers";
import { barSeries, baseChartOptions, CHART_HEIGHT } from "./dashboardChart";
import StatTile from "./StatTile";
import UnavailableNotice from "./UnavailableNotice";

/**
 * BOF-76 — energy delivered, split AC / DC where known (FR-011 – FR-015).
 *
 * One unit for the whole panel, chosen from every value it shows, so a tile and the chart beside it are
 * never in kWh and MWh at once (FR-015). Per-day figures divide by the days that have elapsed, never by
 * the length of a running period (FR-012).
 */
const EnergyPanel: React.FC<PanelProps> = ({ selected, previous, trend }) => {
  const e = selected.energyKwh;
  const unit = energyUnitFor([...trend.map((p) => p.energyKwh.total), e.total]);
  const split = splitDrawable(trend);
  const showUnclassified = split && hasUnclassified(trend, (p) => p.energyKwh.unclassified);

  const data = useMemo(() => {
    const labels = trend.map(axisLabel);
    const series = (pick: (p: (typeof trend)[number]) => number) => trend.map((p) => inUnit(pick(p), unit));
    if (!split) {
      return { labels, datasets: [barSeries("Total", series((p) => p.energyKwh.total), CHART_NEUTRAL, true)] };
    }
    return {
      labels,
      datasets: [
        barSeries("AC", series((p) => p.energyKwh.ac), CHART_AC, false),
        barSeries("DC", series((p) => p.energyKwh.dc), CHART_DC, !showUnclassified),
        ...(showUnclassified
          ? [barSeries("Unclassified", series((p) => p.energyKwh.unclassified), CHART_NEUTRAL, true)]
          : []),
      ],
    };
  }, [trend, unit, split, showUnclassified]);

  const perOnlineCp = perDayPerOnlineChargePoint(e.total, selected.daysElapsed, selected.census);

  return (
    <Card title="Energy" className="h-full">
      <div className="grid mb-2">
        <div className="col-12 sm:col-4">
          <StatTile
            label="Energy delivered"
            value={formatEnergy(e.total, unit)}
            change={change(e.total, previous?.energyKwh.total)}
            comparedTo={comparisonLabel(previous)}
            formatAbsolute={(v) => (v == null ? formatSignedNumber(null) : `${formatSignedNumber(inUnit(v, unit), unit === "MWh" ? 2 : 0)} ${unit}`)}
          />
        </div>
        <div className="col-6 sm:col-4">
          <StatTile label="Per day" value={formatEnergy(perDay(e.total, selected.daysElapsed), unit)} />
        </div>
        <div className="col-6 sm:col-4">
          <StatTile
            label="Per day per online charge point"
            value={perOnlineCp == null ? "—" : `${perOnlineCp.toLocaleString("en-GB", { maximumFractionDigits: 1 })} kWh`}
            hint="Needs the charge-point census for this period."
          />
        </div>
      </div>
      {split ? (
        <div className="text-xs text-color-secondary mb-2">
          AC {formatEnergy(e.ac, unit)} · DC {formatEnergy(e.dc, unit)}
          {e.unclassified > 0 && <> · Unclassified {formatEnergy(e.unclassified, unit)}</>}
        </div>
      ) : (
        <div className="mb-2">
          <UnavailableNotice reason={{ kind: "splitUnavailable" }} inline />
        </div>
      )}
      <Chart type="bar" data={data} options={baseChartOptions({ stacked: split, yTitle: unit })} style={CHART_HEIGHT} />
    </Card>
  );
};

export default EnergyPanel;
